/**
 * Component: Tw scroll pager
 * -----------------------------------------------------------------------------
 * Dots and optional arrows for a CSS scroll-snap list. Works out how many
 * snap positions the list can reach, hides the dots it can't, marks the
 * current one, and scrolls to an item when a dot or arrow is clicked.
 * Scrolling itself is CSS only, without JavaScript the list still swipes.
 * Shared by Tw sections, guarded so a second copy doesn't redefine it.
 *
 * Markup: [js-pager="list"] holding [js-pager="item"] elements, plus any
 * [js-pager="dot"], [js-pager="prev"], and [js-pager="next"] buttons.
 * With a drag attribute the list can also be dragged with a mouse, it snaps
 * to the nearest item on release. Touch keeps the native swipe.
 *
 */

if (!customElements.get('tw-scroll-pager')) {

  /**
   * Custom element wrapping a scroll-snap list and its controls.
   */
  class TwScrollPager extends HTMLElement {

    /**
     * Cache elements and bind listeners.
     */
    connectedCallback() {
      this.list = this.querySelector('[js-pager="list"]')
      this.items = [...this.querySelectorAll('[js-pager="item"]')]
      this.dots = [...this.querySelectorAll('[js-pager="dot"]')]
      this.prev = this.querySelector('[js-pager="prev"]')
      this.next = this.querySelector('[js-pager="next"]')

      if (!this.list || !this.items.length) {
        return
      }

      this.handleClick = this.handleClick.bind(this)
      this.update = this.update.bind(this)
      this.resizeObserver = new ResizeObserver(this.update)

      this.addEventListener('click', this.handleClick)
      this.list.addEventListener('scroll', this.update, { passive: true })
      this.list.addEventListener('scrollend', this.update, { passive: true })
      this.resizeObserver.observe(this.list)
      this.update()

      if (this.hasAttribute('drag')) {
        this.setupDrag()
      }
    }

    /**
     * Remove listeners when the section is removed in the theme editor.
     */
    disconnectedCallback() {
      if (!this.list || !this.items.length) {
        return
      }

      this.removeEventListener('click', this.handleClick)
      this.list.removeEventListener('scroll', this.update)
      this.list.removeEventListener('scrollend', this.update)
      this.resizeObserver.disconnect()

      if (this.handlePointerDown) {
        this.list.removeEventListener('pointerdown', this.handlePointerDown)
        this.list.removeEventListener('dragstart', this.preventDefault)
        this.list.removeEventListener('click', this.handleDragClick, true)
        window.removeEventListener('pointermove', this.handlePointerMove)
        window.removeEventListener('pointerup', this.handlePointerUp)
      }
    }

    /**
     * Listen for mouse drags on the list.
     */
    setupDrag() {
      this.handlePointerDown = this.handlePointerDown.bind(this)
      this.handlePointerMove = this.handlePointerMove.bind(this)
      this.handlePointerUp = this.handlePointerUp.bind(this)
      this.handleDragClick = this.handleDragClick.bind(this)

      this.list.addEventListener('pointerdown', this.handlePointerDown)
      // Stops the browser dragging images and links out of the list
      this.list.addEventListener('dragstart', this.preventDefault)
      this.list.addEventListener('click', this.handleDragClick, true)
    }

    /**
     * Cancel an event's default action.
     * @param {Event} event - Event to cancel.
     */
    preventDefault(event) {
      event.preventDefault()
    }

    /**
     * Start tracking a mouse drag.
     * @param {PointerEvent} event - Pointer down on the list.
     */
    handlePointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) {
        return
      }

      this.drag = {
        moved: false,
        scrollLeft: this.list.scrollLeft,
        x: event.clientX,
      }

      window.addEventListener('pointermove', this.handlePointerMove)
      window.addEventListener('pointerup', this.handlePointerUp)
    }

    /**
     * Scroll the list with the mouse once it has moved a few pixels.
     * @param {PointerEvent} event - Pointer move anywhere on the page.
     */
    handlePointerMove(event) {
      if (!this.drag) {
        return
      }

      const distance = event.clientX - this.drag.x

      if (!this.drag.moved && Math.abs(distance) > 4) {
        this.drag.moved = true
        // Turns snapping off in CSS while dragging
        this.list.setAttribute('data-dragging', '')
      }

      if (this.drag.moved) {
        event.preventDefault()
        this.list.scrollLeft = this.drag.scrollLeft - distance
      }
    }

    /**
     * Snap to the nearest item when the mouse is released.
     */
    handlePointerUp() {
      window.removeEventListener('pointermove', this.handlePointerMove)
      window.removeEventListener('pointerup', this.handlePointerUp)

      if (!this.drag?.moved) {
        this.drag = null

        return
      }

      this.dragged = true
      this.drag = null
      // The click after a drag fires right away, clear the flag if it doesn't
      setTimeout(() => {
        this.dragged = false
      }, 0)
      this.goTo(this.currentIndex(this.positionCount()))

      // Snapping comes back once the list has settled on the item
      const settle = () => {
        clearTimeout(this.settleTimer)
        this.list.removeAttribute('data-dragging')
      }

      this.list.addEventListener('scrollend', settle, { once: true })
      this.settleTimer = setTimeout(settle, 700)
    }

    /**
     * Ignore the click that ends a drag, so links in the list don't open.
     * @param {MouseEvent} event - Click inside the list.
     */
    handleDragClick(event) {
      if (!this.dragged) {
        return
      }

      this.dragged = false
      event.preventDefault()
      event.stopPropagation()
    }

    /**
     * Scroll offset that brings an item to the list's snap start.
     * @param {HTMLElement} item - Item to scroll to.
     * @returns {number} The scrollLeft value for the item.
     */
    offsetOf(item) {
      // Distance from the first item, which snaps at scroll 0. Both move
      // with the scroll, so this works whatever unit the scroll padding uses
      return item.getBoundingClientRect().left -
        this.items[0].getBoundingClientRect().left
    }

    /**
     * Number of items the list can snap to, at least one.
     * @returns {number} Reachable snap positions.
     */
    positionCount() {
      const maxScroll = this.list.scrollWidth - this.list.clientWidth

      if (maxScroll <= 1) {
        return 1
      }

      const offsets = this.items.map((item) => this.offsetOf(item))
      const reachable = offsets.filter((offset) => offset <= maxScroll + 2).length
      const endsOnItem = offsets[reachable - 1] >= maxScroll - 2

      return Math.min(endsOnItem ? reachable : reachable + 1, this.items.length)
    }

    /**
     * Index of the snap position closest to the current scroll.
     * @param {number} count - Reachable snap positions.
     * @returns {number} Current position index.
     */
    currentIndex(count) {
      const maxScroll = this.list.scrollWidth - this.list.clientWidth

      if (this.list.scrollLeft >= maxScroll - 2) {
        return count - 1
      }

      let closest = 0
      let distance = Infinity

      this.items.slice(0, count).forEach((item, index) => {
        const gap = Math.abs(this.offsetOf(item) - this.list.scrollLeft)

        if (gap < distance) {
          closest = index
          distance = gap
        }
      })

      return closest
    }

    /**
     * Scroll to a snap position.
     * @param {number} index - Position index.
     */
    goTo(index) {
      const count = this.positionCount()
      const target = Math.min(Math.max(index, 0), count - 1)
      const maxScroll = this.list.scrollWidth - this.list.clientWidth
      const left = target === count - 1 ? maxScroll : this.offsetOf(this.items[target])
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      this.list.scrollTo({
        behavior: reduceMotion ? 'instant' : 'smooth',
        left,
      })
    }

    /**
     * Handle dot and arrow clicks.
     * @param {MouseEvent} event - Click event from inside the element.
     */
    handleClick(event) {
      const control = event.target.closest('[js-pager]')

      if (!control) {
        return
      }

      const count = this.positionCount()
      const current = this.currentIndex(count)

      if (control === this.prev) {
        this.goTo(current - 1)
      } else if (control === this.next) {
        this.goTo(current + 1)
      } else if (this.dots.includes(control)) {
        this.goTo(this.dots.indexOf(control))
      }
    }

    /**
     * Sync dots and arrows with the scroll position.
     */
    update() {
      const count = this.positionCount()
      const current = this.currentIndex(count)

      this.toggleAttribute('data-static', count < 2)

      this.dots.forEach((dot, index) => {
        dot.hidden = index >= count
        dot.setAttribute('aria-current', index === current ? 'true' : 'false')
      })

      if (this.prev) {
        this.prev.disabled = current === 0
      }

      if (this.next) {
        this.next.disabled = current >= count - 1
      }
    }
  }

  customElements.define('tw-scroll-pager', TwScrollPager)
}
